import React, { useEffect, useState } from 'react'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import { Container, Typography, Card, CardHeader, CardContent, MenuItem } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import StyledTable from '../../../components/Common/StyledTable'
import { MTableCell } from '@material-table/core'
import { gray } from '../../../utils/colors'
import MoreMenu from '../../../components/Common/Menus/MoreMenu'
import { MainButton } from '../../../components'
import { AddOutlined } from '@material-ui/icons'
import TrainingCourseDialog from '../../../components/Dialogs/Training/TrainingCourseDialog'
import { TRAINING_COURSE_TEMPLATE, DIALOG_EDIT_MODES, VACCINES } from '../../../utils'
import DangerConfirmationDialog from '../../../components/Common/Dialogs/DangerConfirmation'
import StyledChip from '../../../components/Common/StyledChip'
import useConstants from '../../../hooks/useConstants'

const useStyles = makeStyles(() => ({
	root: {
		paddingTop: '1.5rem',
		paddingLeft: '1.5rem',
		paddingRight: '1.5rem',
		paddingBottom: '1.5rem'
	},
	card: { maxWidth: '1500px' },
	cardContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		gap: '1rem'
	},
	mainHeader: { fontWeight: 800, marginBottom: 16 },
	header: {
		fontSize: '.75rem',
		fontWeight: '800',
		letterSpacing: 0.75,
		marginTop: 24,
		marginBottom: 8,
		textTransform: 'uppercase'
	},
	cardHeader: {
		'& .MuiCardHeader-action': {
			margin: 0
		}
	}
}))

const TrainingCourseTypes = () => {
	const classes = useStyles()
	const theme = useTheme()

	const [editMode, setEditMode] = useState(DIALOG_EDIT_MODES.New)
	const [editDialogOpen, setEditDialogOpen] = useState(false)
	const [currentEditedObject, setCurrentEditedObject] = useState({ ...TRAINING_COURSE_TEMPLATE })
	const [confirmRemoveCourseDialogOpen, setConfirmRemoveCourseDialogOpen] = useState(false)
	const [courseToRemove, setCourseToRemove] = useState(null)

	const { trainingSkills } = useConstants()

	const { facility, loading, getTrainingCoursesForFacility, updateTrainingCourseForFacility, trainingCourses } =
		useSelectedFacility()

	useEffect(() => {
		const fetchCourses = async () => {
			await getTrainingCoursesForFacility()
		}
		if (facility) {
			fetchCourses()
		}
	}, [facility, getTrainingCoursesForFacility])

	const handleEditDialogOpen = ({ editMode = DIALOG_EDIT_MODES.New }) => {
		setEditMode(editMode)
		setEditDialogOpen(true)
	}

	const handleEditDialogClose = () => {
		setEditDialogOpen(false)
		setCurrentEditedObject({ ...TRAINING_COURSE_TEMPLATE })
	}

	const handleEditCourseButtonClick = (existing) => {
		setCurrentEditedObject({ ...existing })
		handleEditDialogOpen({ editMode: DIALOG_EDIT_MODES.Edit })
	}

	const handleCloseConfirmRemoveCourseDialog = () => {
		setCourseToRemove(null)
		setConfirmRemoveCourseDialogOpen(false)
	}

	const handleOpenConfirmRemoveCourseDialog = (course) => {
		setCourseToRemove({ ...course })
		setConfirmRemoveCourseDialogOpen(true)
	}

	const handleRemoveCourse = async () => {
		await updateTrainingCourseForFacility({
			course_id: courseToRemove?._id,
			updates: { deactivated: true },
			facility_id: facility._id
		})
		handleCloseConfirmRemoveCourseDialog()
	}

	const handleNewCourseButtonClick = () => {
		setCurrentEditedObject({ ...TRAINING_COURSE_TEMPLATE })
		handleEditDialogOpen({ editMode: DIALOG_EDIT_MODES.New })
	}

	const columns = [
		{
			title: 'Name',
			field: 'name',
			width: '25%',
			render: (data) => (
				<span>
					{data.name ||
						data.training_course_identifier_id?.name ||
						data.training_course_identifier?.name}
				</span>
			)
		},
		{
			title: 'Capacity (Max)',
			field: 'capacity_max',
			width: '10%'
		},
		{
			title: '# of Sessions',
			sorting: false,
			width: '10%',
			render: (data) => <span>{data.number_of_sessions || data.sessions.length || 0}</span>
		},
		{
			title: 'Eligibility',
			sorting: false,
			width: '20%',
			render: (data) => {
				const {
					prerequisite_skills,
					age_min_months,
					age_max_months,
					size_restrictions,
					required_vaccines
				} = data
				let ageText = ''
				if (!age_min_months && !age_max_months) {
					ageText = 'All Ages'
				} else {
					ageText = `${age_min_months}mo - ${age_max_months}mo`
				}
				return (
					<div>
						{prerequisite_skills.length > 0 && (
							<div>
								Pre-Reqs:{' '}
								{prerequisite_skills
									.map((id) => {
										return trainingSkills.find((s) => s._id === id)?.name || 'Unknown Skill'
									})
									.join(', ')}
							</div>
						)}
						{<div>Age: {ageText}</div>}
						{
							<div>
								Sizes:{' '}
								{size_restrictions.length > 0 ? (
									<span style={{ textTransform: 'uppercase' }}>
										{size_restrictions.join(', ')}
									</span>
								) : (
									'none'
								)}
							</div>
						}
						{
							<span>
								Vaccines:{' '}
								{required_vaccines.length > 0
									? required_vaccines.map((vax) => VACCINES[vax]).join(', ')
									: 'none'}
							</span>
						}
					</div>
				)
			}
		},
		{ title: 'Type', width: '10%', render: (data) => <StyledChip label="Training" /> },
		{ title: 'Course ID', width: '25%', field: 'course_id' },
		{
			title: '',
			sorting: false,
			render: (d) => {
				const menuOptions = []
				menuOptions.push(
					<MenuItem key="edit-course" onClick={() => handleEditCourseButtonClick(d)}>
						Edit Course
					</MenuItem>
				)
				menuOptions.push(
					<MenuItem key="remove-course" onClick={() => handleOpenConfirmRemoveCourseDialog(d)}>
						Remove Course
					</MenuItem>
				)
				return <MoreMenu options={menuOptions} description={d.name} useHoverState />
			}
		}
	]

	return (
		<>
			<TrainingCourseDialog
				open={editDialogOpen}
				onClose={handleEditDialogClose}
				onSaved={handleEditDialogClose}
				onCancelled={handleEditDialogClose}
				title={
					editMode === DIALOG_EDIT_MODES.New
						? 'Add New Course'
						: editMode === DIALOG_EDIT_MODES.Edit
						? 'Edit Course'
						: 'View Course'
				}
				course={currentEditedObject}
				mode={editMode}
			/>
			<DangerConfirmationDialog
				title="Remove Course"
				open={confirmRemoveCourseDialogOpen}
				description={`Are you sure you want to remove ${
					courseToRemove?.name || 'this course'
				}? You won't be able to create any additional classes for this course.`}
				dangerButtonText="Remove"
				onConfirmDangerousAction={handleRemoveCourse}
				onCancel={handleCloseConfirmRemoveCourseDialog}
			/>
			<div>
				<Container className={classes.root} maxWidth="auto">
					<Typography variant="h5" className={classes.mainHeader}>
						Course Types
					</Typography>
					<Card className={classes.card}>
						<CardHeader
							className={classes.cardHeader}
							title="Courses"
							action={
								<MainButton
									label="New Course"
									onClick={handleNewCourseButtonClick}
									startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
								/>
							}
						/>
						<CardContent>
							<div className={classes.cardContentContainer}>
								<StyledTable
									isLoading={loading}
									columns={columns}
									data={trainingCourses}
									onRowClick={() => null}
									pageSize={10}
									paging={false}
									style={{
										boxShadow: 'none',
										minHeight: '500px'
									}}
									headerStyle={{
										backgroundColor: gray[100],
										borderTop: 'none'
									}}
									hideBorderBottom
									overrideOptions={{ pageSizeOptions: [10] }}
									components={{
										Cell: (props) => {
											return (
												<MTableCell
													style={{
														borderBottom: 'none'
													}}
													{...props}
												/>
											)
										}
									}}
								/>
							</div>
						</CardContent>
					</Card>
				</Container>
			</div>
		</>
	)
}

export default TrainingCourseTypes
